/**
 * planFeatures.js — Single source of truth for plan limits, features, and display config.
 *
 * All plan-gating logic, pricing UI, usage tracking, and upgrade prompts should
 * read from this file rather than hardcoding values in individual components.
 *
 * Plan IDs must match user_profiles.plan_type values in the database.
 *
 * Plans:
 *   free         → Free             — $0/mo
 *   starter      → Starter Pro      — $29/mo ($19/mo billed annually)
 *   professional → Business         — $69/mo ($49/mo billed annually)
 *   enterprise   → Agency (White Label) — $169/mo ($129/mo billed annually)
 */

// ─── Plan IDs ─────────────────────────────────────────────────────────────────
export const PLAN_IDS = {
  FREE:         'free',
  STARTER:      'starter',
  PROFESSIONAL: 'professional',
  ENTERPRISE:   'enterprise',
};

// ─── Plan ordering (index = upgrade order) ────────────────────────────────────
export const PLAN_ORDER = ['free', 'starter', 'professional', 'enterprise'];

/** True when a stored value is a supported plan key or a recognized legacy alias. */
export const isRecognizedPlanKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return [
    'free', 'free starter',
    'starter', 'starter pro', 'pro',
    'professional', 'business',
    'enterprise', 'agency', 'agency (white label)', 'agency white label',
  ].includes(normalized);
};

/** Maps current and legacy display values to the stable internal plan key. */
export const normalizePlanKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['free', 'starter', 'professional', 'enterprise'].includes(normalized)) return normalized;
  if (['free starter'].includes(normalized)) return 'free';
  if (['starter pro', 'pro'].includes(normalized)) return 'starter';
  if (['business'].includes(normalized)) return 'professional';
  if (['agency', 'agency (white label)', 'agency white label'].includes(normalized)) return 'enterprise';
  return 'free';
};

/** Returns true if planA is at least as high as planB in the upgrade order */
export const planAtLeast = (planA, planB) => {
  const a = PLAN_ORDER.indexOf(normalizePlanKey(planA));
  const b = PLAN_ORDER.indexOf(normalizePlanKey(planB));
  return a >= b;
};

/**
 * Resolves a gated feature from the same precedence order everywhere in the UI.
 * Explicit account and tier settings override the static plan ladder. Callers
 * must keep entitlement resolution pending until the tier query completes.
 */
export const resolveFeatureAccess = ({
  featureKey,
  minPlan,
  planType,
  accountFeatures,
  featureEntitlements,
  entitlementsReady,
  isAdmin,
  isOwner,
}) => {
  if (isAdmin || isOwner) return { allowed: true, loading: false };
  if (!entitlementsReady) return { allowed: false, loading: true };

  if (featureKey && Object.prototype.hasOwnProperty.call(accountFeatures || {}, featureKey)) {
    return { allowed: accountFeatures[featureKey] === true, loading: false };
  }

  const configured = featureKey ? featureEntitlements?.[featureKey] : undefined;
  if (configured && typeof configured === 'object' && typeof configured.included === 'boolean') {
    return { allowed: configured.included, loading: false };
  }

  const fallbackPlan = minPlan || FEATURE_DEFINITIONS.find((feature) => feature.key === featureKey)?.minPlan;
  return { allowed: fallbackPlan ? planAtLeast(planType, fallbackPlan) : false, loading: false };
};

// ─── Limits (-1 = unlimited) ──────────────────────────────────────────────────
export const PLAN_LIMITS = {
  free: {
    maxForms:       3,
    maxSubmissions: 100,
    maxTeamMembers: 1,
    maxStorageMb:   100,
  },
  starter: {
    maxForms:       -1,
    maxSubmissions: 2_500,
    maxTeamMembers: -1,
    maxStorageMb:   5_120,
  },
  professional: {
    maxForms:       -1,
    maxSubmissions: 25_000,
    maxTeamMembers: -1,
    maxStorageMb:   51_200,
  },
  enterprise: {
    maxForms:       -1,
    maxSubmissions: 100_000,
    maxTeamMembers: -1,
    maxStorageMb:   256_000,
  },
};

/** Formats a limit value for display. -1 → 'Unlimited'. */
export const formatLimit = (value) => {
  if (value === -1) return 'Unlimited';
  if (value >= 1_000_000) return `${value / 1_000_000}M`;
  if (value >= 1_000)     return `${value / 1_000}K`;
  return String(value);
};

/** Formats a storage limit expressed in MB. -1 → 'Unlimited'. */
export const formatStorageLimit = (valueMb) => {
  if (valueMb === -1) return 'Unlimited';
  if (valueMb < 1_024) return `${valueMb} MB`;
  return `${valueMb / 1_024} GB`;
};

/** Returns limits for a given plan type, falling back to free. */
export const getPlanLimits = (planType) =>
  PLAN_LIMITS[normalizePlanKey(planType)] || PLAN_LIMITS.free;

/** Applies a validated pricing_tiers limit record over the catalog fallback. */
export const resolveConfiguredLimits = (configuredLimits, fallbackLimits) => {
  const normalizeLimit = (value, fallback) => Number.isInteger(Number(value)) ? Number(value) : fallback;
  if (!configuredLimits) return fallbackLimits;
  return {
    maxForms: normalizeLimit(configuredLimits.max_forms, fallbackLimits.maxForms),
    maxSubmissions: normalizeLimit(configuredLimits.max_submissions, fallbackLimits.maxSubmissions),
    maxTeamMembers: normalizeLimit(configuredLimits.max_team_members, fallbackLimits.maxTeamMembers),
    maxStorageMb: normalizeLimit(configuredLimits.max_storage_mb, fallbackLimits.maxStorageMb),
  };
};

// ─── Feature definitions ──────────────────────────────────────────────────────
/**
 * Each feature:
 *   key          — matches account_features keys in user_profiles
 *   label        — short display label (comparison table)
 *   description  — tooltip / info text
 *   category     — grouping header
 *   plans        — per-plan inclusion map (true/false/'value string')
 *   minPlan      — lowest plan that includes this feature
 */
export const FEATURE_DEFINITIONS = [
  // ── Core ────────────────────────────────────────────────────────────────────
  {
    key: 'form_builder',
    label: 'Drag-and-drop form builder',
    description: 'Build forms visually with our intuitive drag-and-drop editor.',
    category: 'Core',
    minPlan: 'free',
    plans: { free: true, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'conversational_mode',
    label: 'Multi-step form flows',
    description: 'Guide respondents one question at a time for higher completion rates.',
    category: 'Core',
    minPlan: 'free',
    plans: { free: true, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'ssl_encryption',
    label: 'SSL encryption & secure storage',
    description: 'All data is encrypted in transit and at rest.',
    category: 'Core',
    minPlan: 'free',
    plans: { free: true, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'embed',
    label: 'Embed on any website',
    description: 'Copy a snippet to embed your form on any site or landing page.',
    category: 'Core',
    minPlan: 'free',
    plans: { free: true, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'basic_analytics',
    label: 'Basic analytics dashboard',
    description: 'View submission counts, device breakdown, and trends.',
    category: 'Core',
    minPlan: 'free',
    plans: { free: true, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'core_integrations',
    label: 'Core integrations',
    description: 'Connect to Google Sheets, Notion, Slack, and popular CRMs.',
    category: 'Core',
    minPlan: 'free',
    plans: { free: true, starter: true, professional: true, enterprise: true },
  },
  // ── Forms & Fields ───────────────────────────────────────────────────────────
  {
    key: 'file_uploads',
    label: 'File upload fields',
    description: 'Let respondents attach files, images, and documents.',
    category: 'Forms & Fields',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'conditional_logic',
    label: 'Conditional logic (show/hide)',
    description: 'Show or hide fields based on respondent answers.',
    category: 'Forms & Fields',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'multi_step',
    label: 'Multi-step forms',
    description: 'Split long forms into pages for better completion rates.',
    category: 'Forms & Fields',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'calculated_fields',
    label: 'Calculated fields & scores',
    description: 'Auto-compute totals, scores, and formulas from respondent input.',
    category: 'Forms & Fields',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'custom_css',
    label: 'Custom CSS',
    description: 'Override any style with your own CSS for pixel-perfect branding.',
    category: 'Forms & Fields',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'turnstile',
    label: 'Cloudflare Turnstile spam protection',
    description: 'Add a privacy-friendly anti-bot challenge to a form before it accepts submissions.',
    category: 'Forms & Fields',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'payment_forms',
    label: 'Payment processing (Stripe)',
    description: 'Accept payments and sell products directly through your forms.',
    category: 'Forms & Fields',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'advanced_logic',
    label: 'Advanced conditional logic',
    description: 'Multi-condition rules, calculated branches, and cross-field dependencies.',
    category: 'Forms & Fields',
    minPlan: 'professional',
    plans: { free: false, starter: false, professional: true, enterprise: true },
  },
  // ── Analytics & Optimization ──────────────────────────────────────────────────
  {
    key: 'advanced_analytics',
    label: 'Advanced analytics & reports',
    description: 'Funnel analysis, geographic data, drop-off rates, and custom reports.',
    category: 'Analytics & Optimization',
    minPlan: 'professional',
    plans: { free: false, starter: false, professional: true, enterprise: true },
  },
  {
    key: 'ab_testing',
    label: 'A/B testing & optimization',
    description: 'Test different form versions to maximize conversions.',
    category: 'Analytics & Optimization',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  // ── Integrations ──────────────────────────────────────────────────────────────
  {
    key: 'webhooks',
    label: 'Webhooks & Zapier',
    description: 'Connect to 1,000+ apps via Zapier or custom webhooks.',
    category: 'Integrations',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'custom_domains',
    label: 'Custom domains',
    description: 'Host your forms on your own domain (e.g. forms.yourbrand.com).',
    category: 'Integrations',
    minPlan: 'professional',
    plans: { free: false, starter: false, professional: true, enterprise: true },
  },
  {
    key: 'api_access',
    label: 'Full REST API access',
    description: 'Programmatically manage forms and submissions via our REST API.',
    category: 'Integrations',
    minPlan: 'enterprise',
    plans: { free: false, starter: false, professional: false, enterprise: true },
  },
  // ── Security & Teams ──────────────────────────────────────────────────────────
  {
    key: 'remove_branding',
    label: 'Remove WebForms branding',
    description: 'Hide the "Powered by WebForms" footer from all your forms.',
    category: 'Security & Teams',
    minPlan: 'starter',
    plans: { free: false, starter: true, professional: true, enterprise: true },
  },
  {
    key: 'white_label',
    label: 'White-labeling',
    description: 'Fully branded experience — your logo, your colors, your domain.',
    category: 'Security & Teams',
    minPlan: 'enterprise',
    plans: { free: false, starter: false, professional: false, enterprise: true },
  },
  {
    key: 'team_members',
    label: 'Team seats',
    description: 'Invite team members with role-based permissions.',
    category: 'Security & Teams',
    minPlan: 'free',
    plans: {
      free:         '1 seat',
      starter:      'Unlimited',
      professional: 'Unlimited',
      enterprise:   'Unlimited',
    },
  },
  {
    key: 'client_workspaces',
    label: 'Client workspaces',
    description: 'Manage forms on behalf of multiple clients from one account.',
    category: 'Security & Teams',
    minPlan: 'enterprise',
    plans: { free: false, starter: false, professional: false, enterprise: true },
  },
  {
    key: 'a2p_consents',
    label: 'A2P SMS consents',
    description: 'Collect and store compliant A2P 10DLC SMS consent via your forms.',
    category: 'Security & Teams',
    minPlan: 'professional',
    plans: { free: false, starter: false, professional: true, enterprise: true },
  },
  {
    key: 'priority_support',
    label: 'Priority support (24h response)',
    description: 'Get answers from our dedicated support team within 24 hours.',
    category: 'Security & Teams',
    minPlan: 'professional',
    plans: { free: false, starter: false, professional: true, enterprise: true },
  },
  {
    key: 'soc2',
    label: 'SOC 2 certification',
    description: 'SOC 2 Type II certified infrastructure for enterprise security requirements.',
    category: 'Security & Teams',
    minPlan: 'enterprise',
    plans: { free: false, starter: false, professional: false, enterprise: true },
  },
  {
    key: 'sso',
    label: 'SSO / SAML',
    description: 'Authenticate your team via Okta, Azure AD, Google Workspace, and more.',
    category: 'Security & Teams',
    minPlan: 'enterprise',
    plans: { free: false, starter: false, professional: false, enterprise: true },
  },
  {
    key: 'dedicated_db',
    label: 'Dedicated database isolation',
    description: 'Your data lives in a dedicated, isolated database — no shared tenancy.',
    category: 'Security & Teams',
    minPlan: 'enterprise',
    plans: { free: false, starter: false, professional: false, enterprise: true },
  },
  {
    key: 'custom_sla',
    label: 'Custom SLAs',
    description: 'Uptime commitments and custom SLA terms defined in your enterprise contract.',
    category: 'Security & Teams',
    minPlan: 'enterprise',
    plans: { free: false, starter: false, professional: false, enterprise: true },
  },
];

/** Returns features grouped by category */
export const getFeaturesByCategory = () => {
  const groups = {};
  for (const feature of FEATURE_DEFINITIONS) {
    if (!groups[feature.category]) groups[feature.category] = [];
    groups[feature.category].push(feature);
  }
  return groups;
};

/** Returns feature definitions included in a specific plan */
export const getPlanFeatures = (planType) => {
  const plan = normalizePlanKey(planType);
  return FEATURE_DEFINITIONS.filter((f) => {
    const v = f.plans[plan];
    return v === true || (typeof v === 'string' && v);
  });
};

// ─── Plan display metadata ────────────────────────────────────────────────────
export const PLAN_DISPLAY = {
  free: {
    label:       'Free',
    price:       0,
    annualPrice: 0,
    annualMonthlyPrice: 0,
    description: 'For testing and hobbyists',
    badge:       null,
    isPopular:   false,
    ctaText:     'Get Started Free',
    color:       'teal',
    badgeClasses: 'bg-teal-50 text-teal-700 border-teal-200',
    accentColor:  'text-teal-600',
  },
  starter: {
    label:       'Starter Pro',
    price:       29,
    annualPrice: 228,
    annualMonthlyPrice: 19,
    description: 'For solopreneurs and small ops',
    badge:       null,
    isPopular:   false,
    ctaText:     'Start Free Trial',
    color:       'blue',
    badgeClasses: 'bg-blue-50 text-blue-700 border-blue-200',
    accentColor:  'text-blue-600',
  },
  professional: {
    label:       'Business',
    price:       69,
    annualPrice: 588,
    annualMonthlyPrice: 49,
    description: 'For growing teams and SMBs',
    badge:       'Most Popular',
    isPopular:   true,
    ctaText:     'Start Free Trial',
    color:       'primary',
    badgeClasses: 'bg-primary/10 text-primary border-primary/20',
    accentColor:  'text-primary',
  },
  enterprise: {
    label:       'Agency (White Label)',
    price:       169,
    annualPrice: 1548,
    annualMonthlyPrice: 129,
    description: 'For agencies and multi-tenant resellers',
    badge:       'White Label',
    isPopular:   false,
    ctaText:     'Contact Sales',
    color:       'purple',
    badgeClasses: 'bg-purple-50 text-purple-700 border-purple-200',
    accentColor:  'text-purple-600',
  },
};

/** Returns full plan display config merged with limits for a plan type */
export const getPlanConfig = (planType) => {
  const key = normalizePlanKey(planType);
  return {
    id:       key,
    limits:   getPlanLimits(key),
    features: getPlanFeatures(key),
    display:  PLAN_DISPLAY[key] || PLAN_DISPLAY.free,
  };
};

/** Feature categories in display order */
export const FEATURE_CATEGORIES = [
  'Core',
  'Forms & Fields',
  'Analytics & Optimization',
  'Integrations',
  'Security & Teams',
];
